/**
 * Google Tag Manager Service
 * Provides methods for tracking events and managing GTM integration
 */

class GTMService {
  constructor() {
    this.gtmId = "GTM-WVQN4L9R"; // Replace with your actual GTM container ID
    this.isInitialized = false;
    this.dataLayer = [];
    this.beforeInitQueue = [];
  }

  /**
   * Initialize GTM service
   * This should be called after GTM script is loaded
   */
  initialize(gtmId = null) {
    if (gtmId) {
      this.gtmId = gtmId;
    }

    // Check if GTM is available
    if (typeof window !== "undefined" && window.dataLayer) {
      this.dataLayer = window.dataLayer;
      this.isInitialized = true;
      //console.log("GTM Service initialized with container ID:", this.gtmId);
      for (const data of this.beforeInitQueue) {
        //console.log("GTM Event from delayed tracking:", data);
        this.dataLayer.push(data);
      }
      this.beforeInitQueue = [];
    } else {
      console.warn("GTM not available - events will be logged to console");
    }
  }

  /**
   * Push data to GTM dataLayer
   * @param {Object} data - The data to push to dataLayer
   */
  push(data) {
    if (this.isInitialized && this.dataLayer) {
      this.dataLayer.push(data);
      //console.log("GTM Event:", data);
    } else {
      //console.log("GTM Event (delayed tracking - GTM not initialized):", data);
      this.beforeInitQueue.push(data);
    }
  }

  /**
   * Track a custom event
   * @param {string} action - The event action
   * @param {string} category - The event category
   * @param {string} label - The event label (optional)
   * @param {number} value - The event value (optional)
   */
  trackEvent(action, category, label = null, value = null) {
    const eventData = {
      event: "custom_event",
      event_action: action,
      event_category: category,
    };

    if (label) eventData.event_label = label;
    if (value !== null) eventData.event_value = value;

    this.push(eventData);
  }

  /**
   * Track page views
   * @param {string} pagePath - The page path
   * @param {string} pageTitle - The page title
   */
  trackPageView(pagePath, pageTitle = null) {
    this.push({
      event: "page_view",
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }

  /**
   * Track form submissions
   * @param {string} formName - The form name/identifier
   * @param {string} step - The current step (optional)
   */
  trackFormSubmission(formName, step = null) {
    const eventData = {
      event: "form_submit",
      form_name: formName,
    };

    if (step) eventData.form_step = step;

    this.push(eventData);
  }

  /**
   * Track wizard step completion
   * @param {number} stepNumber - The step number
   * @param {string} stepName - The step name
   */
  trackWizardStep(stepNumber, stepName) {
    this.push({
      event: "ga4_step" + stepNumber,
      step_number: stepNumber,
      step_name: stepName,
    });
  }

  /**
   * Track battery selection
   * @param {Object} batteryData - Battery information
   */
  trackBatterySelection(batteryData) {
    this.push({
      event: "battery_selected",
      battery_id: batteryData.id,
      battery_name: batteryData.name,
      battery_capacity: batteryData.capacity,
      battery_price: batteryData.price,
    });
  }

  /**
   * Track consultation requests
   * @param {Object} userData - User data
   */
  trackConsultationRequest(userData) {
    this.push({
      event: "consultation_request",
      user_household_size: userData.household,
      user_solar_panels: userData.solarPanels,
      user_has_heatpump: userData.heatpump,
      user_has_ev_charger: userData.evcharger,
    });
  }

  /**
   * Track errors
   * @param {string} errorMessage - Error message
   * @param {string} errorLocation - Where the error occurred
   */
  trackError(errorMessage, errorLocation) {
    this.push({
      event: "error",
      error_message: errorMessage,
      error_location: errorLocation,
    });
  }

  /**
   * Track whitelabel brand usage
   * @param {string} brandName - The brand name
   */
  trackWhitelabelBrand(brandName) {
    this.push({
      event: "whitelabel_brand",
      brand_name: brandName,
    });
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties
   */
  setUserProperties(properties) {
    this.push({
      event: "user_properties",
      ...properties,
    });
  }

  /**
   * Track conversion/goal completion
   * @param {string} conversionName - The conversion name
   * @param {number} conversionValue - The conversion value (optional)
   */
  trackConversion(conversionName, conversionValue = null) {
    const eventData = {
      event: "conversion",
      conversion_name: conversionName,
    };

    if (conversionValue !== null) {
      eventData.conversion_value = conversionValue;
    }

    this.push(eventData);
  }

  /**
   * Get the current GTM container ID
   * @returns {string} The GTM container ID
   */
  getContainerId() {
    return this.gtmId;
  }

  /**
   * Check if GTM is properly initialized
   * @returns {boolean} True if GTM is initialized
   */
  isGTMReady() {
    return this.isInitialized;
  }
}

// Export singleton instance
export default new GTMService();
